import {
  Accordion,
  AccordionItem,
  Badge,
  CheckBox,
} from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { NewsFilter } from '~/services/news/news.types';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { countFilterKeyChecked } from '~/views/news/utils/news-filters-utils/news-filters-utils';

import styles from './SideAccordionFilters.module.scss';

export type SideAccordionFiltersProps = {
  filters: NewsFilter[];
  toggleFilterOption: (
    filterKey: string,
    optionKey?: string,
    trackFilter?: () => void
  ) => void;
};

export const SideAccordionFilters = ({
  filters,
  toggleFilterOption,
}: SideAccordionFiltersProps) => {
  const { t } = useTranslation(['news']);
  const { trackNewsFilter } = usePosthogEvents();

  const trackNewsFilterSelected = () => trackNewsFilter('news', 'watchlist');

  const accordionItemFactory = (filter: NewsFilter) => {
    if (!filter.options.length) {
      return null;
    }

    const { some, all, count } = countFilterKeyChecked({
      filters,
      filterKey: filter.key,
    });

    const genRightAlignedChild = () => {
      if (!count) {
        return null;
      }

      if (count === 1) {
        return (
          <span
            className={styles.singleSelectedItem}
            data-testid="right-label"
          >
            {filter.options.filter(item => item.isChecked)[0].name}
          </span>
        );
      }

      return (
        <Badge
          className={styles.badge}
          data-testid="right-label"
          variant="numerical"
          label={count}
          size="small"
        />
      );
    };

    return {
      title: filter.name,
      className: count ? 'option-selected' : undefined,
      content: (
        <div className={styles.accordionContentWrapper}>
          <CheckBox
            className={styles.styledCheckbox}
            label={t('news:allFilter', { filter: filter.name })}
            name={t('news:allFilter', { filter: filter.name })}
            checked={some}
            partial={some && !all}
            onChange={() =>
              toggleFilterOption(filter.key, undefined, trackNewsFilterSelected)
            }
            data-testid={`toggle-all-${filter.name}`}
          />

          {filter.options.map(item => (
            <CheckBox
              className={styles.styledCheckbox}
              data-testid="filter-option-checkbox"
              key={item.key}
              label={item.name}
              name={item.name}
              checked={item.isChecked}
              onChange={() =>
                toggleFilterOption(
                  filter.key,
                  item.key,
                  trackNewsFilterSelected
                )
              }
            />
          ))}
        </div>
      ),
      rightAlignedChild: genRightAlignedChild(),
    };
  };

  const items = filters
    .map(accordionItemFactory)
    .filter(Boolean) as AccordionItem[];

  return (
    <div className={styles.sideAccordionFiltersRoot}>
      <Accordion
        className={styles.styledAccordion}
        data-testid="accordion-filters"
        items={items}
      />
    </div>
  );
};
